
import React, {useState, useEffect, useContext} from 'react';
import {format} from 'date-fns';
import {ApiContext} from "../../contexts/ApiContext";
import {useAuth} from "../../../providers/authProvider";
import DefaultLayout from "../../../layouts/DefaultLayout";
import BoxLayout from "../../../layouts/BoxLayout";
import {useNavigate, useParams} from "react-router-dom";
import H2Header from "../../headers/h2";
import DefaultButton from "../../buttons/DefaultButton";
import {FaCheck, FaTimes} from "react-icons/fa";
import AcceptHomeVisitModal from "../../modals/AcceptRequestModal";
import BaseModal from "../../common/BaseModal";
import {acceptAndCreateRelation, declineHomeVisitRequest} from "../../../utils/homeVisitRequest";
import MailEditor from "../../mail/MailEditor";
import {generateVisitTimeEmailContent} from "../../mail/Tijdbezoek";

interface RequestDetails {
    HomeVisitCategoryName: string;
    FirstName: string;
    LastName: string;
    Email: string;
    Phone: string;
    Date: string;
    StartTime: string;
    Address: string;
    ZipCode: string;
    City: string;
    Comments: string;
}

function AdminHomeVisitRequestDetailPage() {
    const {Url} = useContext(ApiContext);
    const {token} = useAuth();
    const [requestDetails, setRequestDetails] = useState<RequestDetails | any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState('');
    const [openAcceptModal, setOpenAcceptModal] = useState(false);
    const [openDeclineModal, setOpenDeclineModal] = useState(false);
    const [timeSlotData, setTimeSlotData] = useState<any>(null);
    const navigate = useNavigate();
    const params = useParams();

    const fetchRequestDetails = async () => {
        setLoading(true);
        try {
            const response = await fetch(Url + 'admin/home-visit-request-details/' + params.id, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 200) {
                const data = await response.json();
                setRequestDetails(data);
                setError(null);
            } else {
                setError('Geen informatie voor deze aanvraag beschikbaar.');
            }
        } catch (err) {
            setError('Het ophalen van de aanvraag is mislukt');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequestDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Url, token, params.id]);

    const handleSend = async (emailData: any) => {
        setMessage('Bezig met versturen...');
        try {
            const response = await fetch(Url + 'admin/send-mail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    sender: 'info@ridderendeprins.nl',
                    recipient: emailData.to,
                    cc: emailData.cc,
                    subject: emailData.subject,
                    htmlBody: emailData.body
                })
            });

            if (response.ok) {
                setMessage('Email succesvol verzonden!');
                setTimeout(() => setMessage(''), 3000);
            } else {
                throw new Error('Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setMessage('Het versturen van de email is mislukt');
        }
    };


    const handleAccept = async (timeData: any) => {
        setMessage('Bezig met accepteren...');
        try {
            setTimeSlotData(timeData);
            await acceptAndCreateRelation(params.id, timeData, requestDetails, Url, token);

            setMessage('Aanvraag succesvol geaccepteerd en relatie aangemaakt!');
            setOpenAcceptModal(false);
        } catch (err) {
            console.error('Error accepting request:', err);
            setMessage('Het accepteren van de aanvraag of het aanmaken van de relatie is mislukt');
        }
    };

    const handleDecline = async () => {
        setOpenDeclineModal(true);
    };

    const confirmDecline = async () => {
        setOpenDeclineModal(false);

        setMessage('Bezig met afwijzen...');
        try {
            await declineHomeVisitRequest(params.id, Url, token);
            setMessage('Aanvraag succesvol afgewezen!');
            setTimeout(() => {
                navigate('/admin/home-visit-requests', { replace: false });
            }, 1500);
        } catch (err) {
            console.error('Error declining request:', err);
            setMessage('Het afwijzen van de aanvraag is mislukt');
        }
    };

    const handleBack = () => {
        navigate('/admin/home-visit-requests', { replace: false });
    };

    let dataViewer;
    if (loading) {
        dataViewer = (
            <BoxLayout>
                <div>Loading...</div>
            </BoxLayout>
        );
    } else if (error) {
        dataViewer = (
            <BoxLayout>
                <div className="text-red-500">{error}</div>
                <div className="mt-4">
                    <DefaultButton content="Terug" onClick={handleBack} />
                </div>
            </BoxLayout>
        );
    } else if (requestDetails) {
        dataViewer = (
            <BoxLayout>
                <div className="mb-4 mt-4">
                    <DefaultButton content="Terug" onClick={handleBack} />
                </div>

                <H2Header headerText={"Huisbezoek Aanvraag Details - " + requestDetails.HomeVisitCategoryName} />

                {message && (
                    <div className={`my-4 p-3 rounded ${message.includes('mislukt') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {message}
                    </div>
                )}

                <H2Header headerText="Contactgegevens" />
                <div className="text-lg mb-2">
                    <span className="font-semibold">Naam:</span> {requestDetails.FirstName} {requestDetails.LastName}
                </div>
                <div className="text-lg mb-2">
                    <span className="font-semibold">Email:</span> {requestDetails.Email}
                </div>
                <div className="text-lg mb-4">
                    <span className="font-semibold">Telefoon:</span> {requestDetails.Phone}
                </div>

                <H2Header headerText="Datum en Tijd" />
                <div className="text-lg mb-2">
                    <span className="font-semibold">Datum:</span> {requestDetails.Date ? format(new Date(requestDetails.Date), 'dd-MM-yyyy') : 'Niet opgegeven'}
                </div>
                <div className="text-lg mb-4">
                    <span
                        className="font-semibold">Starttijd:</span> {requestDetails.StartTime ? requestDetails.StartTime : 'Niet opgegeven'}
                </div>

                <H2Header headerText="Locatie" />
                <div className="text-lg mb-2">
                    <span className="font-semibold">Adres:</span> {requestDetails.Address}
                </div>
                <div className="text-lg mb-2">
                    <span className="font-semibold">Postcode:</span> {requestDetails.ZipCode}
                </div>
                <div className="text-lg mb-4">
                    <span className="font-semibold">Stad:</span> {requestDetails.City}
                </div>

                {requestDetails.Comments && (
                    <>
                        <H2Header headerText="Opmerkingen" />
                        <div className="text-lg mb-4 p-3 bg-gray-50 rounded">
                            {requestDetails.Comments}
                        </div>
                    </>
                )}

                <div className="mt-6 flex gap-4">
                    <button
                        onClick={() => setOpenAcceptModal(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <FaCheck size={18} />
                        Plan Huisbezoek
                    </button>
                    <button
                        onClick={handleDecline}
                        className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        <FaTimes size={18} />
                        Afwijzen
                    </button>
                </div>
                {message && (
                    <div className={`my-4 p-3 rounded ${message.includes('mislukt') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {message}
                    </div>
                )}
                <H2Header headerText="Nieuwe Email - Tijdsblok Afspreken" />
                <MailEditor
                    key={timeSlotData ? 'with-timeslot' : 'without-timeslot'}
                    onSend={handleSend}
                    initialData={{
                        to: requestDetails.Email,
                        cc: 'info@ridderendeprins.nl',
                        subject: 'Huisbezoek fam. ' + requestDetails.LastName + ' op ' + format(new Date(requestDetails.Date), 'dd-MM-yyyy'),
                        body: (generateVisitTimeEmailContent({
                                naam: requestDetails.FirstName,
                                datum: format(new Date(requestDetails.Date), 'dd-MM-yyyy'),
                                typeBezoek: requestDetails.HomeVisitCategoryName,
                                plaats: requestDetails.City,
                                tijdsblokStart: timeSlotData?.startTimeSlot || "[TIJDBLOK START]",
                                tijdsblokEind: timeSlotData?.endTimeSlot || "[TIJDBLOK EIND]"
                            }
                        ) as any).html
                    }}
                />
                {message && (
                    <div className={`my-4 p-3 rounded ${message.includes('mislukt') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {message}
                    </div>
                )}


                <AcceptHomeVisitModal 
                    isOpen={openAcceptModal}
                    onClose={() => setOpenAcceptModal(false)}
                    onAccept={handleAccept}
                />
                <BaseModal
                    show={openDeclineModal}
                    onClose={() => setOpenDeclineModal(false)}
                    title="Bevestig Afwijzing"
                    footer={
                        <>
                            <button
                                onClick={confirmDecline}
                                className="min-w-40 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors h-9"
                            >
                                Ja, afwijzen
                            </button>
                            <button
                                onClick={() => setOpenDeclineModal(false)}
                                className="min-w-40 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors h-9"
                            >
                                Annuleren
                            </button>
                        </>
                    }
                >
                    <p className="text-base leading-relaxed text-gray-500">
                        Weet u zeker dat je deze huisbezoek aanvraag wilt afwijzen?
                    </p>
                </BaseModal>
            </BoxLayout>
        );
    }

    return (
        <DefaultLayout>
            <div className="flex flex-col overflow-auto py-4 sm:py-0">
                {dataViewer}
            </div>
        </DefaultLayout>
    );
}

export default AdminHomeVisitRequestDetailPage;